import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';

// project imports
import IconButton from '@/components/@extended/IconButton';
import Avatar from '@/components/@extended/Avatar';
import Dot from '@/components/@extended/Dot';
import MainCard from '@/components/MainCard';
import SimpleBar from '@/components/third-party/SimpleBar';
import MessageOutlined from '@ant-design/icons/MessageOutlined';
import BankOutlined from '@ant-design/icons/BankOutlined';
import EnvironmentOutlined from '@ant-design/icons/EnvironmentOutlined';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import BellOutlined from '@ant-design/icons/BellOutlined';
import { useHasPermission, P } from '@/components/permission-gate';
import { CHAT_ROOM_TYPE } from '@/types/chat';
import {
	useChatNotifications,
	ChatNotificationEntry
} from '@/providers/chat-notifications/provider';

// ==============================|| HEADER - MESAJE (listă conversații) ||============================== //

const relTime = (iso: string): string => {
	const t = new Date(iso).getTime();
	if (Number.isNaN(t)) return '';
	const min = Math.floor((Date.now() - t) / 60000);
	if (min < 1) return 'acum';
	if (min < 60) return `${min}m`;
	const h = Math.floor(min / 60);
	if (h < 24) return `${h}h`;
	const d = Math.floor(h / 24);
	if (d < 7) return `${d}z`;
	return `${Math.floor(d / 7)}săpt`;
};

const typeIcon = (roomTypeId: number) =>
	roomTypeId === CHAT_ROOM_TYPE.Project ? <EnvironmentOutlined /> : <BankOutlined />;

type Filter = 'toate' | 'necitite' | 'arhivate';
const FILTERS: { key: Filter; label: string }[] = [
	{ key: 'toate', label: 'Toate' },
	{ key: 'necitite', label: 'Necitite' },
	{ key: 'arhivate', label: 'Arhivate' }
];

export default function ChatMenu() {
	const navigate = useNavigate();
	// widget-ul de mesaje din header cere permisiunea de chat — fără ea nu-l randăm deloc (acțiune
	// indisponibilă = nu e afișată), iar provider-ul oricum nu face poll (deci nici 403 pe alte pagini)
	const canChat = useHasPermission(P.chat);
	const { allEntries, unreadTotal, markAllRead, markRoomRead } = useChatNotifications();
	const [open, setOpen] = useState(false);
	const [filter, setFilter] = useState<Filter>('toate');
	const handleToggle = () => setOpen((v) => !v);

	if (!canChat) return null;

	const openRoom = (e: ChatNotificationEntry) => {
		markRoomRead(e.roomId);
		setOpen(false);
		navigate(`/common/chat?room=${e.roomId}`);
	};

	const filtered = allEntries.filter((e) =>
		filter === 'necitite'
			? e.unreadCount > 0
			: filter === 'arhivate'
				? e.isArchived
				: true
	);

	return (
		<>
			<Box sx={{ flexShrink: 0 }}>
				<Tooltip title="Mesaje">
					<IconButton
						color="secondary"
						variant="light"
						sx={(theme) => ({
							color: 'text.primary',
							bgcolor: open ? 'grey.100' : 'transparent',
							...theme.applyStyles('dark', {
								bgcolor: open ? 'background.default' : 'transparent'
							})
						})}
						aria-label="deschide mesajele"
						onClick={handleToggle}
					>
						<Badge badgeContent={unreadTotal > 99 ? '99+' : unreadTotal} color="primary">
							<MessageOutlined />
						</Badge>
					</IconButton>
				</Tooltip>
			</Box>

			<Drawer
				sx={{ zIndex: 2001 }}
				anchor="right"
				onClose={handleToggle}
				open={open}
				slotProps={{ paper: { sx: { width: { xs: 340, sm: 440 } } } }}
			>
				{open && (
					<MainCard
						title={<Typography variant="h5">Mesaje</Typography>}
						sx={{
							border: 'none',
							borderRadius: 0,
							height: '100vh',
							'& .MuiCardHeader-root': { p: 2.25 }
						}}
						content={false}
						secondary={
							unreadTotal > 0 ? (
								<Link
									component="button"
									underline="hover"
									sx={{ typography: 'caption', textTransform: 'none' }}
									onClick={() => markAllRead()}
								>
									Marchează toate citite
								</Link>
							) : undefined
						}
					>
						<Stack
							direction="row"
							sx={{
								py: 1.5,
								px: 2.5,
								gap: 1.25,
								alignItems: 'center',
								borderBottom: '1px solid',
								borderColor: 'divider'
							}}
						>
							{FILTERS.map((f) => (
								<Chip
									key={f.key}
									size="small"
									clickable
									label={f.label}
									color={filter === f.key ? 'primary' : 'secondary'}
									variant={filter === f.key ? 'combined' : 'light'}
									onClick={() => setFilter(f.key)}
								/>
							))}
						</Stack>

						<SimpleBar
							sx={{ overflowX: 'hidden', height: 'calc(100vh - 175px)', minHeight: 420 }}
						>
							{!filtered.length ? (
								<Stack
									sx={{ alignItems: 'center', justifyContent: 'center', py: 8, px: 3 }}
								>
									<Typography color="text.secondary" variant="body2">
										{filter === 'necitite' ? 'Nimic necitit.' : 'Nicio conversație.'}
									</Typography>
								</Stack>
							) : (
								<List component="nav" disablePadding>
									{filtered.map((e) => {
										// camerele cu notificări oprite (mute) NU se numără în badge/clopoțel → aici
										// le arătăm de-emphasizat (fără bold, chip secundar) + iconiță „notificări oprite"
										const unread = e.unreadCount > 0;
										const highlight = unread && !e.isMuted;
										return (
											<ListItemButton
												key={e.roomId}
												divider
												onClick={() => openRoom(e)}
												sx={{ px: 1.75, py: 1.25, gap: 1.5, alignItems: 'flex-start' }}
											>
												<Avatar
													src={e.imageUrl || undefined}
													color={e.isArchived ? 'secondary' : 'primary'}
													type="combined"
													size="sm"
												>
													{typeIcon(e.roomTypeId)}
												</Avatar>
												<Box sx={{ flex: 1, minWidth: 0 }}>
													<Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
														<Typography
															variant="h6"
															noWrap
															sx={{
																flex: 1,
																minWidth: 0,
																fontWeight: highlight ? 600 : 500
															}}
														>
															{e.title}
															{e.isArchived ? ' (arhivată)' : ''}
														</Typography>
														{e.isMuted && (
															<Tooltip title="Notificări oprite">
																<Box
																	component="span"
																	sx={{
																		display: 'inline-flex',
																		color: 'text.disabled',
																		fontSize: 12
																	}}
																>
																	<BellOutlined />
																</Box>
															</Tooltip>
														)}
														{unread && (
															<Chip
																size="small"
																color={highlight ? 'primary' : 'secondary'}
																variant={highlight ? 'filled' : 'light'}
																label={e.unreadCount}
																sx={{ height: 20 }}
															/>
														)}
													</Stack>
													<Stack
														direction="row"
														sx={{ alignItems: 'center', gap: 0.5, mt: 0.25 }}
													>
														{unread ? (
															<Dot color={highlight ? 'primary' : 'secondary'} />
														) : (
															<CheckOutlined
																style={{
																	fontSize: 10,
																	color: 'var(--mui-palette-success-main)'
																}}
															/>
														)}
														<Typography
															variant="caption"
															noWrap
															sx={{
																flex: 1,
																minWidth: 0,
																color: highlight ? 'text.primary' : 'text.secondary',
																...(highlight && { fontWeight: 500 })
															}}
														>
															{e.preview || 'Niciun mesaj încă'}
														</Typography>
														<Typography
															variant="caption"
															color="text.secondary"
															sx={{ whiteSpace: 'nowrap' }}
														>
															{relTime(e.lastActivity)}
														</Typography>
													</Stack>
												</Box>
											</ListItemButton>
										);
									})}
								</List>
							)}
						</SimpleBar>

						<Stack sx={{ py: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
							<Link
								component="button"
								underline="hover"
								sx={{ alignSelf: 'center', typography: 'body2' }}
								onClick={() => {
									setOpen(false);
									navigate('/common/chat');
								}}
							>
								Deschide Mesajele
							</Link>
						</Stack>
					</MainCard>
				)}
			</Drawer>
		</>
	);
}
